/**
 * Decision Workspace tokens — the values in DESIGN.md, and nothing else.
 *
 * DESIGN.md is the authority. The status and button pairs are the contrast-measured
 * Material 3 values from CLAUDE.md §1.9.2 and are used as units: never mix a fill from one
 * pair with ink from another, and never add a neighbouring shade. A new colour gets a token
 * and a measured contrast figure here before any component uses it.
 */

export const WS_COLOR = {
  background: '#f6f8fb',
  surface: '#ffffff',
  surfaceSubtle: '#eef2f6',
  text: '#202124',
  textMuted: '#5f6368',
  border: '#d9dee7',
  /** 1px outline of a text field or select (CLAUDE.md §1.9.2). */
  fieldOutline: '#79747e',
  primary: '#1565c0',
  onPrimary: '#ffffff',
  focus: '#0842a0',
  tonal: '#f1f3f4',
  onTonal: '#3c4043',
  /** Tonal button on hover; ink #3c4043 on it measures 8.35:1. */
  tonalHover: '#e3e6e8',
  /** Fill of a destructive confirm button; white ink on it measures 9.11:1. */
  destructive: '#8c1d18',
  /** Scrim behind a dialog or sheet. */
  scrim: 'rgba(32, 33, 36, 0.4)',
  /** The one soft offset shadow, for menus and dialogs (DESIGN.md Elevation). */
  menuShadow: '0 4px 12px rgba(32, 33, 36, 0.12)',
} as const;

/** Fill + ink pairs, with the measured contrast ratio of each. */
export const WS_STATUS = {
  info: { fill: '#d3e3fd', ink: '#0842a0', contrast: 7.04 },
  success: { fill: '#e6f4ea', ink: '#0d652d', contrast: 6.34 },
  warning: { fill: '#fee9d9', ink: '#8c3a00', contrast: 6.57 },
  neutral: { fill: '#e8eaed', ink: '#3c4043', contrast: 8.68 },
  error: { fill: '#f9dedc', ink: '#8c1d18', contrast: 7.17 },
} as const;

export type WsTone = keyof typeof WS_STATUS;

export const WS_FONT = {
  sans: '"Source Sans 3 Variable", "Source Sans 3", Aptos, system-ui, "Segoe UI", sans-serif',
  mono: '"JetBrains Mono", ui-monospace, SFMono-Regular, Consolas, monospace',
} as const;

/** Type scale: [fontSize px, lineHeight px, weight]. */
export const WS_TYPE = {
  pageTitle: [26, 32, 600],
  sectionTitle: [18, 24, 600],
  body: [15, 22, 400],
  table: [14, 20, 400],
  tableHeader: [13, 20, 600],
  label: [13, 20, 500],
  meta: [12, 16, 400],
  mono: [12, 16, 400],
} as const;

export const WS_RADIUS = {
  chip: 8,
  field: 8,
  card: 12,
  dialog: 16,
  button: 18,
} as const;

/** 4px grid. */
export const WS_SPACE = { xs: 4, sm: 8, md: 12, lg: 16, xl: 24, xxl: 32 } as const;

export const WS_CONTROL = {
  /** Buttons, text fields, selects (CLAUDE.md §1.9.2). */
  height: 36,
  rowHeight: 40,
  rowHeightCompact: 36,
  /** 44px hit area on a control that is visually smaller (DESIGN accessibility rule). */
  hitArea: 44,
} as const;

export const WS_FRAME = {
  barHeight: 56,
  /** The navigation drawer. */
  drawerWidth: 320,
  /** The optional footer row under the screen (vendor attribution, version). */
  footerHeight: 32,
  gutter: 24,
  /** Width of the detail pane beside a registry. */
  paneWidth: 320,
} as const;

/**
 * Dimensions the layouts share. A layout never types its own pane or nav width: it names one here,
 * so changing a width is a one-line decision (see docs/ui-kit-plan.md, owner decision 3 for the
 * nav rail).
 */
export const WS_LAYOUT = {
  /** Container width (px) at and below which a layout drops its side columns. */
  narrow: 899,
  /** Section navigation beside a form (Assessment). */
  sectionNav: 200,
  /** Settings navigation (Administration). */
  adminNav: 220,
  /** Assumptions pane (Financial model). */
  assumptions: 280,
  /** Evidence column bounds (Assessment). */
  evidenceMin: 280,
  evidenceMax: 380,
  /** Width of a report document (Decision and outcome). */
  document: 880,
  /** Explanation column of a FieldGroup. */
  fieldGroupLabel: 240,
  /** Height a stacked table, chart or timeline gets once a layout is narrow. */
  stackedRegion: 420,
} as const;

/** TimelineGrid geometry. */
export const WS_TIMELINE = {
  labelWidth: 240,
  columnWidth: 60,
  rowHeight: 44,
  headerHeight: 32,
  capacityHeight: 96,
} as const;
